import camelcaseKeys from 'camelcase-keys';
import { z } from 'zod';

import {
  AiVisibilityPlatformComparisonStatusSchema,
  AiVisibilityPlatformSchema,
  AiVisibilityReportIdSchema,
  AiVisibilityRunIdSchema,
  AiVisibilityRunStatusSchema,
  AiVisibilityScheduleFrequencySchema,
  AiVisibilityScheduleTimeSlotSchema,
  LocationIdSchema,
  PlaceIdSchema
} from '@brightlocal/shared';
import type { LocationId } from '@brightlocal/shared';

import { PaginationSchema } from '../../../schemas';

/** Query params of the paginated reports list. */
export interface AiVisibilityGetReportsParams {
  /** Filter the list to a single location's report. */
  locationId?: LocationId;
  /** 1-indexed page number; the API defaults to 1 when omitted. */
  page?: number;
  /** Items per page (1-100); the API defaults to 10 when omitted. */
  numPerPage?: number;
}

/** Query params of the paginated runs list of a report. */
export interface AiVisibilityGetRunsParams {
  /** 1-indexed page number; the API defaults to 1 when omitted. */
  page?: number;
  /** Items per page (1-100); the API defaults to 25 when omitted. */
  numPerPage?: number;
}

// Schedule fields of a reports-list entry. `schedule_run_on` is an ISO weekday 1-7 for weekly
// and a day of month 1-28 for monthly; null for manual and daily.
const AiVisibilityScheduleFieldsSchema = {
  schedule_frequency: AiVisibilityScheduleFrequencySchema,
  schedule_time_slot: AiVisibilityScheduleTimeSlotSchema.nullable(),
  schedule_run_on: z.number().nullable()
};

/** Single entry of the paginated reports list — carries only the schedule config. */
export const AiVisibilityReportListItemSchema = z
  .object({
    report_id: AiVisibilityReportIdSchema,
    location_id: LocationIdSchema,
    ...AiVisibilityScheduleFieldsSchema,
    created_at: z.string(),
    updated_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityReportListItem = z.infer<typeof AiVisibilityReportListItemSchema>;

/** Paginated list of the customer's AI Visibility reports, each addressed by its `reportId`. */
export const AiVisibilityReportsResponseSchema = z.object({
  data: z.array(AiVisibilityReportListItemSchema),
  pagination: PaginationSchema
});

export type AiVisibilityReportsResponse = z.infer<typeof AiVisibilityReportsResponseSchema>;

/**
 * Full settings of a single report returned by the report-by-id endpoint — unlike the list
 * entries it carries the tracked `prompts`, `platforms` and `competitors`. The `*_at` fields
 * are RFC 3339 timestamps; `schedule_next_run_at` is null when no run is scheduled and
 * `last_run_at` is null when the report has never run.
 */
export const AiVisibilityReportSchema = z
  .object({
    report_id: AiVisibilityReportIdSchema,
    location_id: LocationIdSchema,
    platforms: z.array(AiVisibilityPlatformSchema),
    prompts: z.array(z.string()),
    competitors: z.array(z.string()),
    ...AiVisibilityScheduleFieldsSchema,
    schedule_next_run_at: z.string().nullable(),
    last_run_at: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityReport = z.infer<typeof AiVisibilityReportSchema>;

/**
 * Settings to create an AI Visibility report (camelCase; serialized to snake_case by
 * `serializeRequestBody`). Schedule detail fields may be null and are enforced server-side:
 * required when the frequency is scheduled, null for manual/daily.
 */
export const AiVisibilityCreateReportSettingsSchema = z.object({
  locationId: LocationIdSchema,
  scheduleFrequency: AiVisibilityScheduleFrequencySchema,
  scheduleTimeSlot: AiVisibilityScheduleTimeSlotSchema.nullable().optional(),
  scheduleRunOn: z.number().nullable().optional(),
  platforms: z.array(AiVisibilityPlatformSchema).min(1),
  prompts: z.array(z.string()).min(1)
});

export type AiVisibilityCreateReportSettings = z.infer<typeof AiVisibilityCreateReportSettingsSchema>;

/** 201 response of report creation — carries the id of the newly created report. */
export const AiVisibilityCreateReportResponseSchema = z
  .object({
    report_id: AiVisibilityReportIdSchema
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityCreateReportResponse = z.infer<typeof AiVisibilityCreateReportResponseSchema>;

/**
 * Partial settings to update a report (camelCase; serialized by `serializeRequestBody`).
 * Omitted fields keep their current value; the report is addressed by `report_id` via the path.
 */
export const AiVisibilityUpdateReportSettingsSchema = z.object({
  scheduleFrequency: AiVisibilityScheduleFrequencySchema.optional(),
  scheduleTimeSlot: AiVisibilityScheduleTimeSlotSchema.nullable().optional(),
  scheduleRunOn: z.number().nullable().optional(),
  platforms: z.array(AiVisibilityPlatformSchema).min(1).optional(),
  prompts: z.array(z.string()).min(1).optional()
});

export type AiVisibilityUpdateReportSettings = z.infer<typeof AiVisibilityUpdateReportSettingsSchema>;

/** Successful update returns 204 No Content — there is no response body to parse. */
export const AiVisibilityUpdateReportResponseSchema = z.undefined();

// Uniform metrics carried by the summary, platform, prompt and non-branded blocks.
// Rate/score values are already on a 0–100 scale from the API; avg_position is a raw position.
// `share_of_voice` is null when not computed (e.g. a platform with no AI responses) and
// `avg_position` is null when the business was never mentioned.
const MetricsBaseSchema = {
  visibility_score: z.number(),
  mention_rate: z.number(),
  mention_count: z.number(),
  checks_with_ai_response: z.number(),
  share_of_voice: z.number().nullable(),
  avg_position: z.number().nullable()
};

const NonBrandedMetricsSchema = z.object(MetricsBaseSchema).transform((data) => camelcaseKeys(data));

/**
 * Run-wide metrics block with its non-branded-prompts subset.
 * `non_branded` is null when every tracked prompt is branded (there is no non-branded subset).
 */
export const AiVisibilityMetricsBlockSchema = z
  .object({
    ...MetricsBaseSchema,
    non_branded: NonBrandedMetricsSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityMetricsBlock = z.infer<typeof AiVisibilityMetricsBlockSchema>;

/**
 * Per-platform metrics — adds the total number of checks executed on the platform.
 * `non_branded` is null when every tracked prompt is branded (same rule as the summary block).
 */
export const AiVisibilityPlatformMetricsSchema = z
  .object({
    ...MetricsBaseSchema,
    total_checks: z.number(),
    non_branded: NonBrandedMetricsSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityPlatformMetrics = z.infer<typeof AiVisibilityPlatformMetricsSchema>;

/**
 * Result of a single prompt on a single platform: the base metric set plus the qualitative
 * mention detail. `sentiment` and `mention_context` are null when the business was not mentioned.
 */
export const AiVisibilityPromptPlatformMetricsSchema = z
  .object({
    ...MetricsBaseSchema,
    is_mentioned: z.boolean(),
    sentiment: z.string().nullable(),
    mention_context: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityPromptPlatformMetrics = z.infer<typeof AiVisibilityPromptPlatformMetricsSchema>;

/**
 * Per-prompt metrics with the per-platform split keyed by platform identifier.
 * `is_branded` is the Spectra classifier result for the prompt text (branded vs unbranded).
 * `non_branded` is null for branded prompts (there is no non-branded subset of a branded prompt).
 */
export const AiVisibilityPromptBreakdownSchema = z
  .object({
    ...MetricsBaseSchema,
    is_branded: z.boolean(),
    non_branded: NonBrandedMetricsSchema.nullable(),
    prompt: z.string(),
    platforms: z.record(AiVisibilityPlatformSchema, AiVisibilityPromptPlatformMetricsSchema)
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityPromptBreakdown = z.infer<typeof AiVisibilityPromptBreakdownSchema>;

/**
 * Per-platform metrics for a competitor mention. Platform keys stay snake_case under the parent
 * transform; each value camelCases its own fields. `average_position` is null when the competitor
 * was mentioned on the platform without a ranked position.
 */
export const AiVisibilityCompetitorPlatformMetricsSchema = z
  .object({
    visibility_score: z.number(),
    mention_rate: z.number(),
    average_position: z.number().nullable(),
    occurrence_count: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityCompetitorPlatformMetrics = z.infer<typeof AiVisibilityCompetitorPlatformMetricsSchema>;

// The API encodes an empty platform map as `[]` (PHP JSON); non-empty maps are objects keyed by
// platform identifier. Normalize the empty-array form so the record schema can accept both.
const AiVisibilityCompetitorPlatformBreakdownSchema = z.preprocess(
  (value) => (Array.isArray(value) && value.length === 0 ? {} : value),
  z.record(AiVisibilityPlatformSchema, AiVisibilityCompetitorPlatformMetricsSchema)
);

/**
 * Competing business surfaced in the AI answers. `platform_breakdown` maps platform identifier →
 * nested mention metrics (not a raw count); an empty map may arrive as `[]`. `average_position`
 * and `share_of_voice` are null when the competitor has no ranked mentions (same nullability for
 * nested platform `average_position`). Platform-identifier keys stay snake_case under the shallow
 * parent transform.
 */
export const AiVisibilityCompetitorSchema = z
  .object({
    competitor_id: z.string(),
    name: z.string(),
    url: z.string(),
    domain: z.string(),
    address: z.string(),
    occurrence_count: z.number(),
    mention_rate: z.number(),
    visibility_score: z.number(),
    average_position: z.number().nullable(),
    share_of_voice: z.number().nullable(),
    platform_breakdown: AiVisibilityCompetitorPlatformBreakdownSchema
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityCompetitor = z.infer<typeof AiVisibilityCompetitorSchema>;

/** Qualitative insight derived from the AI answers. */
export const AiVisibilityInsightSchema = z
  .object({
    type: z.string(),
    subject: z.string(),
    insight: z.string(),
    category: z.string(),
    sentiment_score: z.number(),
    evidence: z.string(),
    confidence: z.number(),
    is_actionable: z.boolean(),
    suggested_action: z.string().nullable(),
    platforms: z.array(AiVisibilityPlatformSchema),
    prompts: z.array(z.string()),
    occurrence_count: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityInsight = z.infer<typeof AiVisibilityInsightSchema>;

/**
 * Web source cited by the AI platforms, aggregated by domain.
 * Listing fields (`directory_*`) identify known directories when the domain matches one;
 * `platform_breakdown` maps platform identifier → citation count on that platform.
 * Platform-identifier keys stay snake_case under the shallow parent transform.
 */
export const AiVisibilitySourceSchema = z
  .object({
    domain: z.string(),
    directory_identifier: z.string().nullable(),
    directory_label: z.string().nullable(),
    directory_types: z.array(z.string()),
    domain_authority: z.number().nullable(),
    answers_covered: z.number(),
    coverage: z.number(),
    citation_count: z.number(),
    position: z.number(),
    platforms_citing: z.array(AiVisibilityPlatformSchema),
    platform_breakdown: z.record(AiVisibilityPlatformSchema, z.number())
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilitySource = z.infer<typeof AiVisibilitySourceSchema>;

/**
 * LLM-authored run summary; markdown bold (`**…**`) is the only markup.
 * Null when generation failed on an otherwise complete run — a valid outcome,
 * not an error.
 */
const AiVisibilityNarrativeSchema = z
  .object({
    summary_text: z.string()
  })
  .transform((data) => camelcaseKeys(data));

/** Curated result of a completed run, aggregated across the report's platforms and prompts. */
export const AiVisibilityRunResultSchema = z
  .object({
    business: z
      .object({
        name: z.string(),
        place_id: PlaceIdSchema
      })
      .transform((data) => camelcaseKeys(data)),
    summary: AiVisibilityMetricsBlockSchema,
    // Keyed by platform identifiers (enum values, not field names). Each value schema camelCases
    // its own fields; the parent transform below is shallow so these platform keys stay snake_case
    // and keep matching AI_VISIBILITY_PLATFORMS and the `platforms` array. Do NOT use { deep: true }.
    platform_breakdown: z.record(AiVisibilityPlatformSchema, AiVisibilityPlatformMetricsSchema),
    prompt_breakdown: z.array(AiVisibilityPromptBreakdownSchema),
    competitors: z.array(AiVisibilityCompetitorSchema),
    insights: z.array(AiVisibilityInsightSchema),
    narrative: AiVisibilityNarrativeSchema.nullable(),
    sources: z.array(AiVisibilitySourceSchema),
    ai_queries: z.object({
      asked: z.number(),
      answered: z.number()
    })
  })
  // Shallow: nested nodes camelCase their own fields, so the platform-identifier record keys
  // (google_ai_overview, google_ai_mode) survive untouched. { deep: true } would mangle them.
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRunResult = z.infer<typeof AiVisibilityRunResultSchema>;

/**
 * A run of an AI Visibility report — shared by the latest-run and run-by-id endpoints.
 * `result` is null until `status` is `completed`; a report with no runs at all returns 404.
 */
export const AiVisibilityRunSchema = z
  .object({
    run_id: AiVisibilityRunIdSchema,
    report_id: AiVisibilityReportIdSchema,
    location_id: LocationIdSchema,
    status: AiVisibilityRunStatusSchema,
    platforms: z.array(AiVisibilityPlatformSchema),
    created_at: z.string(),
    started_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    error: z.string().nullable(),
    result: AiVisibilityRunResultSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRun = z.infer<typeof AiVisibilityRunSchema>;

/**
 * Single entry of the paginated runs list — status and metadata only, without the `result`
 * payload of the run-by-id endpoint. `started_at`/`completed_at` are null until the run
 * reaches the corresponding state; `error` is null unless the run failed.
 */
export const AiVisibilityRunSummarySchema = z
  .object({
    run_id: AiVisibilityRunIdSchema,
    status: AiVisibilityRunStatusSchema,
    platforms: z.array(AiVisibilityPlatformSchema),
    created_at: z.string(),
    started_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    error: z.string().nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRunSummary = z.infer<typeof AiVisibilityRunSummarySchema>;

/** Paginated list of a report's runs, newest first. */
export const AiVisibilityRunsResponseSchema = z.object({
  data: z.array(AiVisibilityRunSummarySchema),
  pagination: PaginationSchema
});

export type AiVisibilityRunsResponse = z.infer<typeof AiVisibilityRunsResponseSchema>;

/** 202 acknowledgement that an on-demand run was accepted; poll the run endpoints for status. */
export const AiVisibilityRunReportResponseSchema = z
  .object({
    run_id: AiVisibilityRunIdSchema
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRunReportResponse = z.infer<typeof AiVisibilityRunReportResponseSchema>;

/**
 * Value of one metric in the base and comparison runs and the change between them.
 * `absolute_change` is raw `current - previous` — the API does not flip the sign for
 * lower-is-better metrics. Every field is independently nullable: one side not computed, or a
 * percentage that cannot be derived (`previous` is 0 or null).
 */
export const AiVisibilityMetricChangeSchema = z
  .object({
    current: z.number().nullable(),
    previous: z.number().nullable(),
    absolute_change: z.number().nullable(),
    percentage_change: z.number().nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityMetricChange = z.infer<typeof AiVisibilityMetricChangeSchema>;

const metricsComparisonFields = {
  visibility_score: AiVisibilityMetricChangeSchema,
  mention_rate: AiVisibilityMetricChangeSchema,
  mention_count: AiVisibilityMetricChangeSchema,
  checks_with_ai_response: AiVisibilityMetricChangeSchema,
  share_of_voice: AiVisibilityMetricChangeSchema.nullable(),
  average_position: AiVisibilityMetricChangeSchema.nullable()
};

/**
 * Change in each visibility metric between the compared runs. Mirrors the run metrics set,
 * except the API names the position field `average_position` here (not `avg_position` as on run
 * results). The `share_of_voice` / `average_position` blocks are null when the metric was not
 * computed for either run.
 */
export const AiVisibilityMetricsComparisonSchema = z
  .object(metricsComparisonFields)
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityMetricsComparison = z.infer<typeof AiVisibilityMetricsComparisonSchema>;

/**
 * Uniform metrics-comparison block of a scope (the whole run, or one platform): the comparison
 * metrics plus their `non_branded` twin computed over only the non-branded prompts of the scope.
 * The twin is null when non-branded metrics were not computed for both compared runs, and absent
 * on payloads that predate it.
 */
export const AiVisibilityMetricsComparisonBlockSchema = z
  .object({
    ...metricsComparisonFields,
    non_branded: AiVisibilityMetricsComparisonSchema.nullish()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityMetricsComparisonBlock = z.infer<typeof AiVisibilityMetricsComparisonBlockSchema>;

/**
 * Comparison outcome for a single platform: `compared` (present in both runs), `new` (only in
 * the base run) or `missing` (only in the comparison run). `metrics` is null unless the status
 * is `compared`.
 */
export const AiVisibilityPlatformComparisonSchema = z.object({
  status: AiVisibilityPlatformComparisonStatusSchema,
  metrics: AiVisibilityMetricsComparisonBlockSchema.nullable()
});

export type AiVisibilityPlatformComparison = z.infer<typeof AiVisibilityPlatformComparisonSchema>;

// The API encodes an empty platform map as `[]` (PHP JSON); non-empty maps are objects keyed by
// platform identifier. Normalize the empty-array form so the record schema can accept both.
const AiVisibilityPlatformComparisonMapSchema = z.preprocess(
  (value) => (Array.isArray(value) && value.length === 0 ? {} : value),
  z.record(AiVisibilityPlatformSchema, AiVisibilityPlatformComparisonSchema)
);

/** Lightweight reference to one of the compared runs. */
export const AiVisibilityRunRefSchema = z
  .object({
    run_id: AiVisibilityRunIdSchema,
    created_at: z.string()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRunRef = z.infer<typeof AiVisibilityRunRefSchema>;

/**
 * Competitor surfaced in both compared runs, with the change in its presence. Only the id and
 * name are required by the contract; each metric block is null when the metric was not
 * available for either run.
 */
export const AiVisibilityCompetitorDeltaSchema = z
  .object({
    competitor_id: z.string(),
    name: z.string(),
    visibility_score: AiVisibilityMetricChangeSchema.nullable(),
    occurrence_count: AiVisibilityMetricChangeSchema.nullable(),
    mention_rate: AiVisibilityMetricChangeSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityCompetitorDelta = z.infer<typeof AiVisibilityCompetitorDeltaSchema>;

/** Competitors grouped by how they changed between the compared runs. */
export const AiVisibilityCompetitorsComparisonSchema = z.object({
  appeared: z.array(z.string()),
  dropped: z.array(z.string()),
  common: z.array(AiVisibilityCompetitorDeltaSchema)
});

export type AiVisibilityCompetitorsComparison = z.infer<typeof AiVisibilityCompetitorsComparisonSchema>;

/** Cited URLs grouped by how they changed between the compared runs, with overlap counts. */
export const AiVisibilitySourcesComparisonSchema = z
  .object({
    appeared: z.array(z.string()),
    dropped: z.array(z.string()),
    common_count: z.number(),
    base_count: z.number(),
    compare_count: z.number()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilitySourcesComparison = z.infer<typeof AiVisibilitySourcesComparisonSchema>;

/**
 * Deltas between a completed run and the previous completed run of the same report. For the
 * first-ever completed run there is nothing to compare against: `compare_to_run`, `metrics`,
 * `competitors` and `sources` are null and every `platform_comparison` entry has status `new`.
 * An empty `platform_comparison` map may arrive as `[]`.
 */
export const AiVisibilityRunComparisonSchema = z
  .object({
    report_id: AiVisibilityReportIdSchema,
    platforms: z.array(AiVisibilityPlatformSchema),
    base_run: AiVisibilityRunRefSchema,
    compare_to_run: AiVisibilityRunRefSchema.nullable(),
    metrics: AiVisibilityMetricsComparisonBlockSchema.nullable(),
    // Keyed by platform identifiers (enum values, not field names) — the shallow parent transform
    // below keeps these keys snake_case so they keep matching AI_VISIBILITY_PLATFORMS.
    // Do NOT use { deep: true }.
    platform_comparison: AiVisibilityPlatformComparisonMapSchema,
    competitors: AiVisibilityCompetitorsComparisonSchema.nullable(),
    sources: AiVisibilitySourcesComparisonSchema.nullable()
  })
  .transform((data) => camelcaseKeys(data));

export type AiVisibilityRunComparison = z.infer<typeof AiVisibilityRunComparisonSchema>;
